/**
 * Stock Inquiry Page
 * AS9100D-Compliant Inventory Stock Inquiry Interface
 * 
 * Clause Coverage:
 * - 8.5.1: Production Control (Accountability)
 * - 8.5.2: Identification and Traceability
 * - 8.5.4: Preservation (FIFO visibility)
 * 
 * Features:
 * - Real-time stock visibility by lot and location
 * - FIFO rank indicators
 * - Expiry alerts
 * - QC Hold and Quarantine status
 * - Compliance tooltips
 * 
 * @version 1.0.0
 * @date January 2026
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, 
  Filter, 
  Package, 
  MapPin, 
  Calendar, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Shield,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Boxes,
  FileText,
  AlertCircle,
  Loader2,
  Info,
  Warehouse,
  LayoutGrid,
  Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import ComplianceTooltip, { ComplianceLabel } from '../components/ComplianceTooltip';
import InventoryLayout from '../components/layouts/InventoryLayout';
import { formatIST, formatISTDate } from '../utils/timezone';
import api from '../services/api';
import { toast } from 'sonner';

// Status badge configurations
const STATUS_CONFIG = {
  RELEASED: { label: 'Released', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
  QC_HOLD: { label: 'QC Hold', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  QUARANTINE: { label: 'Quarantine', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle },
  EXPIRED: { label: 'Expired', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: AlertCircle },
  AVAILABLE: { label: 'Available', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
  ALLOCATED: { label: 'Allocated', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Package },
};

/**
 * Status Badge Component
 */
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-600', icon: Info };
  const Icon = config.icon;
  
  return (
    <Badge variant="outline" className={`${config.color} gap-1`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

/**
 * FIFO Rank Indicator
 */
const FifoRank = ({ rank, isOldest }) => {
  if (!rank) return <span className="text-gray-400">—</span>;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <div className={`
            flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold
            ${isOldest ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}
          `}>
            {rank}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">
            {isOldest ? 'Oldest lot - Use first (FIFO)' : `FIFO position: ${rank}`}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Expiry Alert Badge
 */
const ExpiryBadge = ({ daysToExpiry, expiryDate }) => {
  if (daysToExpiry === null || daysToExpiry === undefined) {
    return <span className="text-gray-400 text-sm">N/A</span>;
  }
  
  let color = 'text-gray-600';
  let bgColor = 'bg-gray-50';
  let icon = null;
  
  if (daysToExpiry <= 0) {
    color = 'text-red-700';
    bgColor = 'bg-red-50';
    icon = <AlertTriangle className="w-3 h-3" />;
  } else if (daysToExpiry <= 30) {
    color = 'text-red-600';
    bgColor = 'bg-red-50';
    icon = <AlertTriangle className="w-3 h-3" />;
  } else if (daysToExpiry <= 60) {
    color = 'text-amber-600';
    bgColor = 'bg-amber-50';
  } else if (daysToExpiry <= 90) {
    color = 'text-yellow-600';
    bgColor = 'bg-yellow-50';
  }
  
  return (
    <div className={`flex items-center gap-1 px-2 py-1 rounded ${bgColor} ${color} text-xs`}>
      {icon}
      <span>{daysToExpiry <= 0 ? 'Expired' : `${daysToExpiry}d`}</span>
    </div>
  );
};

/**
 * Stock Summary Card
 */
const StockSummaryCard = ({ summary, isLoading }) => {
  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-24">
          <Loader2 className="w-6 h-6 animate-spin text-[#101957]" />
        </div>
      </Card>
    );
  }
  
  if (!summary) return null;
  
  return (
    <Card className="p-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="text-center p-3 bg-blue-50 rounded-lg">
          <p className="text-2xl font-bold text-blue-700">{summary.total_quantity || 0}</p>
          <p className="text-xs text-blue-600">Total On Hand</p>
        </div>
        <div className="text-center p-3 bg-green-50 rounded-lg">
          <p className="text-2xl font-bold text-green-700">{summary.total_available || 0}</p>
          <p className="text-xs text-green-600">Available</p>
        </div>
        <div className="text-center p-3 bg-amber-50 rounded-lg">
          <p className="text-2xl font-bold text-amber-700">{summary.total_allocated || 0}</p>
          <p className="text-xs text-amber-600">Allocated</p>
        </div>
        <div className="text-center p-3 bg-purple-50 rounded-lg">
          <p className="text-2xl font-bold text-purple-700">{summary.unique_lots || 0}</p>
          <p className="text-xs text-purple-600">Unique Lots</p>
        </div>
        <div className="text-center p-3 bg-slate-50 rounded-lg">
          <p className="text-2xl font-bold text-slate-700">{summary.unique_locations || 0}</p>
          <p className="text-xs text-slate-600">Locations</p>
        </div>
      </div>
    </Card>
  );
};

/**
 * Main Stock Inquiry Page Component
 */
const StockInquiryPage = () => {
  // State
  const [stockData, setStockData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Reference data
  const [warehouses, setWarehouses] = useState([]);
  
  // Load warehouses on mount
  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const response = await api.get('/api/v1/inventory/v2/warehouses');
        setWarehouses(response.data || []);
      } catch (err) {
        console.error('Error loading warehouses:', err);
      }
    };
    loadWarehouses();
  }, []);
  
  // Load stock data
  const loadStockData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const filters = {
        page,
        page_size: pageSize,
        sort_by: 'fifo_date',
        sort_order: 'asc',
        show_zero_qty: false
      };
      
      if (searchTerm) {
        filters.part_search = searchTerm;
      }
      
      if (selectedWarehouse && selectedWarehouse !== 'all') {
        filters.warehouse_id = selectedWarehouse;
      }
      
      if (selectedStatus && selectedStatus !== 'all') {
        filters.lot_status = selectedStatus;
      }
      
      const response = await api.post('/api/v1/inventory/v2/stock/inquiry', filters);
      
      setStockData(response.data.items || []);
      setSummary(response.data.summary || {});
      setTotalCount(response.data.total_count || 0);
      setTotalPages(response.data.total_pages || 0);
    } catch (err) {
      console.error('Error loading stock data:', err);
      setError('Failed to load stock data. Please try again.');
      toast.error('Failed to load stock data');
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, searchTerm, selectedWarehouse, selectedStatus]);
  
  useEffect(() => {
    loadStockData();
  }, [loadStockData]);
  
  // Refresh handler
  const handleRefresh = () => {
    loadStockData();
    toast.success('Stock data refreshed');
  };
  
  return (
    <InventoryLayout>
      <div className="h-full overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#101957] flex items-center justify-center">
                <Boxes className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#101957]">Stock Inquiry</h1>
                <p className="text-sm text-gray-500">Real-time inventory visibility with FIFO compliance</p>
              </div>
            </div>
          
          <div className="flex items-center gap-3">
            {/* AS9100D Compliance Badge */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 gap-1">
                    <Shield className="w-3 h-3" />
                    AS9100D Compliant
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <div className="text-xs max-w-xs">
                    <p className="font-medium mb-1">Compliance Coverage:</p>
                    <ul className="space-y-0.5 text-gray-300">
                      <li>• 8.5.1: Production Control</li>
                      <li>• 8.5.2: Identification & Traceability</li>
                      <li>• 8.5.4: Preservation (FIFO)</li>
                    </ul>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>
      
      {/* Filters Bar */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-3">
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by part number, description, or lot number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="stock-search-input"
            />
          </div>
          
          {/* Warehouse Filter */}
          <div className="w-48">
            <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
              <SelectTrigger>
                <Warehouse className="w-4 h-4 mr-2 text-gray-400" />
                <SelectValue placeholder="All Warehouses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Warehouses</SelectItem>
                {warehouses.map(wh => (
                  <SelectItem key={wh.warehouse_id} value={wh.warehouse_id}>
                    {wh.code} - {wh.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Status Filter */}
          <div className="w-40">
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="RELEASED">Released</SelectItem>
                <SelectItem value="QC_HOLD">QC Hold</SelectItem>
                <SelectItem value="QUARANTINE">Quarantine</SelectItem>
                <SelectItem value="EXPIRED">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Advanced Filters Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="text-gray-600"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </Button>
        </div>
        
        {/* Advanced Filters Panel */}
        {showFilters && (
          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-4 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Expiry Within</label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" className="rounded border-gray-300" />
                Show zero qty
              </label>
            </div>
            
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" className="rounded border-gray-300" />
                Only QC Hold
              </label>
            </div>
            
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" className="rounded border-gray-300" />
                Only Quarantine
              </label>
            </div>
          </div>
        )}
      </div>
      
      {/* Summary Cards */}
      <div className="flex-shrink-0 px-6 py-4">
        <StockSummaryCard summary={summary} isLoading={isLoading} />
      </div>
      
      {/* Main Content */}
      <div className="flex-1 overflow-hidden px-6 pb-4">
        <Card className="h-full flex flex-col">
          {/* Table Header with Column Compliance Indicators */}
          <div className="flex-shrink-0 border-b border-slate-200 px-4 py-2 bg-slate-50">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {totalCount > 0 ? `Showing ${stockData.length} of ${totalCount} records` : 'No records found'}
              </span>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  Compliance tooltip available
                </span>
              </div>
            </div>
          </div>
          
          {/* Table */}
          <ScrollArea className="flex-1">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#101957]" />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <AlertCircle className="w-12 h-12 mb-4 text-red-400" />
                <p>{error}</p>
                <Button variant="outline" onClick={handleRefresh} className="mt-4">
                  Try Again
                </Button>
              </div>
            ) : stockData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <Boxes className="w-12 h-12 mb-4 text-gray-300" />
                <p className="text-lg font-medium">No stock records found</p>
                <p className="text-sm">Adjust your filters or add inventory to see stock here</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[50px]">
                      <ComplianceTooltip tooltipId="issue.fifo_recommend">
                        <span className="text-xs">FIFO</span>
                      </ComplianceTooltip>
                    </TableHead>
                    <TableHead>
                      <ComplianceTooltip tooltipId="stock.part_number">
                        <span>Part Number</span>
                      </ComplianceTooltip>
                    </TableHead>
                    <TableHead>
                      <ComplianceTooltip tooltipId="stock.lot_number">
                        <span>Lot Number</span>
                      </ComplianceTooltip>
                    </TableHead>
                    <TableHead>
                      <ComplianceTooltip tooltipId="location.bin_code">
                        <span>Location</span>
                      </ComplianceTooltip>
                    </TableHead>
                    <TableHead className="text-right">
                      <ComplianceTooltip tooltipId="stock.on_hand">
                        <span>On Hand</span>
                      </ComplianceTooltip>
                    </TableHead>
                    <TableHead className="text-right">Available</TableHead>
                    <TableHead>
                      <ComplianceTooltip tooltipId="stock.status">
                        <span>Status</span>
                      </ComplianceTooltip>
                    </TableHead>
                    <TableHead>
                      <ComplianceTooltip tooltipId="stock.expiry">
                        <span>Expiry</span>
                      </ComplianceTooltip>
                    </TableHead>
                    <TableHead>Flags</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stockData.map((row, index) => (
                    <TableRow 
                      key={row.balance_id || index}
                      className="hover:bg-slate-50 cursor-pointer"
                      data-testid={`stock-row-${index}`}
                    >
                      {/* FIFO Rank */}
                      <TableCell>
                        <FifoRank rank={row.fifo_rank} isOldest={row.fifo_rank === 1} />
                      </TableCell>
                      
                      {/* Part Number */}
                      <TableCell>
                        <div>
                          <p className="font-mono text-sm font-medium text-[#101957]">
                            {row.hpn_display || row.part_id}
                          </p>
                          <p className="text-xs text-gray-500 truncate max-w-[200px]">
                            {row.part_description}
                          </p>
                        </div>
                      </TableCell>
                      
                      {/* Lot Number */}
                      <TableCell>
                        <div>
                          <p className="font-mono text-sm">{row.lot_number}</p>
                          {row.supplier_lot_number && (
                            <p className="text-xs text-gray-400">Mfr: {row.supplier_lot_number}</p>
                          )}
                        </div>
                      </TableCell>
                      
                      {/* Location */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3.5 h-3.5 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium">{row.location_code}</p>
                            {row.bln_code && (
                              <p className="text-xs text-gray-400 font-mono">BLN: {row.bln_code}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      
                      {/* On Hand */}
                      <TableCell className="text-right">
                        <span className="font-semibold">{row.quantity}</span>
                        <span className="text-xs text-gray-400 ml-1">{row.uom}</span>
                      </TableCell>
                      
                      {/* Available */}
                      <TableCell className="text-right">
                        <span className={row.available_quantity > 0 ? 'text-green-600' : 'text-gray-400'}>
                          {row.available_quantity}
                        </span>
                      </TableCell>
                      
                      {/* Status */}
                      <TableCell>
                        <StatusBadge status={row.lot_status} />
                      </TableCell>
                      
                      {/* Expiry */}
                      <TableCell>
                        <ExpiryBadge 
                          daysToExpiry={row.days_to_expiry} 
                          expiryDate={row.expiry_date} 
                        />
                      </TableCell>
                      
                      {/* Flags */}
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {row.is_hazmat && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge variant="outline" className="bg-orange-50 text-orange-600 border-orange-200 text-xs">
                                    HAZ
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>Hazardous Material</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {row.is_esd_sensitive && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge variant="outline" className="bg-yellow-50 text-yellow-600 border-yellow-200 text-xs">
                                    ESD
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>ESD Sensitive</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {row.has_coc && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <FileText className="w-4 h-4 text-blue-500" />
                                </TooltipTrigger>
                                <TooltipContent>Certificate of Conformance attached</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {row.is_customer_furnished && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-200 text-xs">
                                    CFM
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>Customer Furnished Material</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </ScrollArea>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex-shrink-0 border-t border-slate-200 px-4 py-3 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
      </div>
    </InventoryLayout>
  );
};

export default StockInquiryPage;
